import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Play, ShieldCheck, Mic, Users, Moon, Heart, Radio, BookOpen, Library, Flame, Sparkles, Feather, Mic2, Lightbulb, Globe2, Film, Crown } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useContentI18n } from "@/context/ContentI18n";
import { usePlayer } from "@/context/PlayerContext";
import { Header } from "@/components/Header";
import { PlayerBar } from "@/components/PlayerBar";
import { SongRow } from "@/components/SongRow";
import { SongOfTheDay } from "@/components/SongOfTheDay";
import { PensionPayday } from "@/components/PensionPayday";
import { RoomPoster } from "@/components/RoomPoster";
import { ShareButton } from "@/components/ShareButton";
import { SponsorCard } from "@/components/SponsorCard";
import { VisionCredits } from "@/components/VisionCredits";
import { PoyaBanner } from "@/components/PoyaBanner";
import { MeheniTribute } from "@/components/MeheniTribute";
import { BlessingOverlay } from "@/components/BlessingOverlay";
import { getSettings, shouldAutoShow, todaysLamp } from "@/lib/blessing";
import { getFavs } from "@/lib/favourites";
import { getTodayPoya, poyaPlaylist } from "@/lib/poya";
import { LiyaPataDivider, LotusMedallion, MoonstoneArc } from "@/components/Motifs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = "https://images.unsplash.com/photo-1775427528127-a66ce3bb2bcb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const FILTERS = [
  { key: "all", labelKey: "filters_all", color: "var(--accent)" },
  { key: "fav", labelKey: "fav_filter", color: "var(--accent-3)" },
  { key: "calm", labelKey: "filters_calm", color: "var(--accent-2)" },
  { key: "veena", labelKey: "filters_veena", color: "var(--accent-4)" },
  { key: "tabla", labelKey: "filters_tabla", color: "var(--lotus)" },
];

export default function Room() {
  const { t, lang, fontClass } = useLang();
  const { title: ctitle } = useContentI18n();
  const { playIndex, current } = usePlayer();
  const [songs, setSongs] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showBlessing, setShowBlessing] = useState(false);
  const [featuredDed, setFeaturedDed] = useState(null);
  const [favIds, setFavIds] = useState(() => getFavs());
  const [visits, setVisits] = useState(0);
  const [sotd, setSotd] = useState(null);
  const poyaEntry = getTodayPoya();

  const ROOMS = [
    { to: "/rooms/poth-kamare", icon: BookOpen, titleKey: "pk_nav", subKey: "pk_banner_sub", grad: "linear-gradient(135deg, #1C140E 0%, #3B2A16 60%, #92400E 140%)", testid: "room-card-poth" },
    { to: "/rooms/story-kamare", icon: BookOpen, titleKey: "st_nav", subKey: "st_banner_sub", grad: "linear-gradient(135deg, #14331F 0%, #2E5A2E 55%, #B45309 150%)", testid: "room-card-story" },
    { to: "/rooms/wadihiti-gayakayo", icon: Mic, titleKey: "wg_nav", subKey: "wg_sub", grad: "linear-gradient(135deg, #2A1020 0%, #5B1F3A 55%, #B45309 150%)", testid: "room-card-wadihiti" },
    { to: "/rooms/bhavana-kamare", icon: Moon, titleKey: "bh_nav", subKey: "bh_sub", grad: "linear-gradient(135deg, #0C1A24 0%, #113B3B 55%, #1E7A6B 150%)", testid: "room-card-bhavana" },
    { to: "/rooms/wada-baila", icon: Sparkles, titleKey: "wb_nav", subKey: "wb_sub", grad: "linear-gradient(135deg, #2A1608 0%, #7A3B0E 55%, #D97706 150%)", testid: "room-card-wada-baila" },
  ];

  useEffect(() => {
    const sync = () => setFavIds(getFavs());
    window.addEventListener("sk-favs-changed", sync);
    return () => window.removeEventListener("sk-favs-changed", sync);
  }, []);

  useEffect(() => {
    axios.get(`${API}/dedications/featured`).then((r) => setFeaturedDed(r.data[0] || null)).catch(() => {});
    axios.get(`${API}/collections`).then((r) => setCollections(r.data)).catch(() => {});
    axios.get(`${API}/song-of-the-day`).then((r) => setSotd(r.data || null)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("sk_visited")) {
      localStorage.setItem("sk_visited", "1");
      axios.post(`${API}/stats/visit`).then((r) => setVisits(r.data.visits)).catch(() => {});
    } else {
      axios.get(`${API}/stats/visits`).then((r) => setVisits(r.data.visits)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    let live = true;
    Promise.all([axios.get(`${API}/songs`), axios.get(`${API}/songs/featured`)])
      .then(([s, f]) => { if (live) { setSongs(s.data); setFeatured(f.data); } })
      .catch(() => {})
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (songs.length && shouldAutoShow(getSettings())) setShowBlessing(true);
  }, [songs]);

  useEffect(() => {
    if (!songs.length) return;
    const sid = new URLSearchParams(window.location.search).get("song");
    if (!sid) return;
    const idx = songs.findIndex((s) => s.id === sid);
    if (idx >= 0) playIndex(idx, songs);
  }, [songs]); // eslint-disable-line

  const filtered = filter === "all"
    ? songs
    : filter === "fav"
      ? songs.filter((s) => favIds.includes(s.id))
      : songs.filter((s) => (s.tags || []).includes(filter));

  return (
    <div className="min-h-screen pb-32" style={{ background: "var(--bg)" }}>
      <Header />

      {/* Hero — Editorial Ceylon Soundstage */}
      <section className="relative overflow-hidden" style={{ background: "var(--hero-gradient)" }}>
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" style={{ opacity: 0.16, mixBlendMode: "luminosity" }} />
        </div>
        <div className="absolute inset-0 sk-mesh pointer-events-none" />
        <MoonstoneArc size={560} testid="room-moonstone" className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-0 sk-spin-slow" style={{ color: "var(--sigiriya-gold)", opacity: 0.35 }} />
        <LiyaPataDivider className="pointer-events-none absolute top-5 right-4 w-40 opacity-30" style={{ color: "var(--sigiriya-gold)" }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pt-12 pb-14 sm:pt-16 sm:pb-20">
          <div className="flex flex-col items-center text-center sk-fade-up">
            <img src="/logo-swaranjaliya.png" alt="Swaranjaliya emblem" className="w-28 h-28 sm:w-36 sm:h-36 rounded-full mb-7 sk-float" style={{ boxShadow: "0 0 70px rgba(255,200,90,0.28)", border: "1px solid rgba(255,215,120,0.35)" }} data-testid="hero-emblem" />
            <span className="inline-flex items-center gap-2 font-ui text-xs sm:text-sm font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-6" style={{ background: "rgba(255,255,255,0.08)", color: "#FFE8B0", border: "1px solid rgba(255,215,120,0.3)", backdropFilter: "blur(10px)" }}>
              <ShieldCheck size={16} /> {t("quiet_note")}
            </span>
            <h1 className={`${lang === "si" ? "font-si" : "sk-display"} text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight`} style={{ color: "#FFFFFF" }}>
              {t("hero_title")}
            </h1>
            <p className="font-ui text-sm font-extrabold tracking-[0.2em] uppercase mt-3 sk-gold-foil inline-block" data-testid="hero-royal-tagline">
              {t("brand_tagline")}
            </p>
            <p className="font-ui text-lg leading-relaxed mt-5 max-w-xl mx-auto" style={{ color: "rgba(245,243,255,0.82)" }}>
              {t("hero_sub")}
            </p>
            <p className="font-ui text-base sm:text-lg italic font-semibold mt-4 max-w-xl mx-auto inline-flex items-start gap-2 text-left" style={{ color: "#FFD873" }} data-testid="hero-shine-line">
              <Sparkles size={18} className="mt-1 shrink-0" />
              <span>{lang === "si" ? "වයස ශරීරයට පමණයි. මෙහිදී හදවත සදා තරුණයි — ඔබ තවමත් බබළයි." : lang === "ta" ? "வயது உடலுக்கு மட்டுமே. இங்கே இதயம் என்றும் இளமை — நீங்கள் இன்னும் ஒளிர்கிறீர்கள்." : "Age is only the body. Here, the heart stays forever young — and you still shine."}</span>
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <button
                onClick={() => songs.length && playIndex(0, songs)}
                disabled={!songs.length}
                data-testid="hero-play-all-button"
                className="inline-flex items-center gap-3 h-14 px-8 rounded-full font-ui text-lg font-extrabold transition-all duration-300 active:scale-95 hover:-translate-y-0.5 disabled:opacity-50 sk-pulse-glow"
                style={{ background: "linear-gradient(100deg, var(--vermilion) 0%, var(--sigiriya-gold) 100%)", color: "#1a0a05", boxShadow: "0 14px 40px -12px var(--accent-glow)" }}
              >
                <Play size={26} fill="currentColor" /> {t("play_all")}
              </button>
              <Link
                to="/rooms/sindu-kamare/membership"
                data-testid="hero-membership-link"
                className="inline-flex items-center gap-2 h-14 px-7 rounded-full font-ui text-base font-extrabold transition-all duration-300 active:scale-95 hover:-translate-y-0.5"
                style={{ background: "rgba(255,255,255,0.06)", color: "#FFD873", border: "1.5px solid rgba(255,215,120,0.55)", backdropFilter: "blur(10px)" }}
              >
                <Crown size={20} /> {lang === "si" ? "පවුලට එක්වන්න" : lang === "ta" ? "குடும்பத்தில் இணையுங்கள்" : "Join the family"}
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-3 mt-4">
              <Link
                to="/rooms/sindu-kamare/submit"
                data-testid="hero-submit-link"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full font-ui text-base font-bold transition-all active:scale-95 hover:-translate-y-0.5"
                style={{ background: "rgba(255,255,255,0.08)", color: "#F5F3FF", border: "1px solid rgba(255,255,255,0.18)" }}
              >
                <Mic size={18} /> {t("submit_nav")}
              </Link>
              <Link
                to="/rooms/sindu-kamare/listen"
                data-testid="hero-listen-link"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full font-ui text-base font-bold transition-all active:scale-95 hover:-translate-y-0.5"
                style={{ background: "rgba(255,255,255,0.08)", color: "#F5F3FF", border: "1px solid rgba(255,255,255,0.18)" }}
              >
                <Radio size={18} /> {t("lt_start")}
              </Link>
            </div>

            <div className="mt-7 rounded-3xl p-5 sm:p-6 max-w-xl mx-auto w-full" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", backdropFilter: "blur(12px)" }} data-testid="invite-block">
              <p className={`${fontClass()} text-base sm:text-lg font-bold`} style={{ color: "#FFFFFF" }}>
                {t("invite_send")} 💛
              </p>
              <div className="mt-3">
                <ShareButton testid="hero-invite" text={t("invite_msg")} url={typeof window !== "undefined" ? window.location.origin : ""} />
              </div>
              <div className="mt-3">
                <RoomPoster />
              </div>
              {visits >= 5 && (
                <p className="font-ui text-sm mt-3" style={{ color: "rgba(245,243,255,0.7)" }} data-testid="listener-count">
                  🎧 {visits.toLocaleString()} {t("listeners_joined")}
                </p>
              )}
            </div>

            <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Link to="/rooms/sindu-kamare/poems" data-testid="poets-corner-link"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full font-ui font-bold text-sm transition-all active:scale-95 hover:-translate-y-0.5"
              style={{ background: "rgba(255,255,255,0.06)", color: "#F5F3FF", border: "1px solid rgba(255,255,255,0.16)" }}>
              <Feather size={18} style={{ color: "var(--sigiriya-gold)" }} /> {t("poets_corner")}
            </Link>
            <Link to="/rooms/sindu-kamare/karaoke" data-testid="karaoke-link"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full font-ui font-bold text-sm transition-all active:scale-95 hover:-translate-y-0.5"
              style={{ background: "rgba(255,255,255,0.06)", color: "#F5F3FF", border: "1px solid rgba(255,255,255,0.16)" }}>
              <Mic2 size={18} style={{ color: "var(--sigiriya-gold)" }} /> {t("karaoke")}
            </Link>
            <Link to="/rooms/sindu-kamare/ideas" data-testid="ideas-link"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full font-ui font-bold text-sm transition-all active:scale-95 hover:-translate-y-0.5"
              style={{ background: "rgba(255,255,255,0.06)", color: "#F5F3FF", border: "1px solid rgba(255,255,255,0.16)" }}>
              <Lightbulb size={18} style={{ color: "var(--sigiriya-gold)" }} /> {t("ideas_wall")}
            </Link>
            <Link to="/rooms/sindu-kamare/friends" data-testid="friends-link"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full font-ui font-bold text-sm transition-all active:scale-95 hover:-translate-y-0.5"
              style={{ background: "rgba(255,255,255,0.06)", color: "#F5F3FF", border: "1px solid rgba(255,255,255,0.16)" }}>
              <Globe2 size={18} style={{ color: "var(--sigiriya-gold)" }} /> {t("friends_wall")}
            </Link>
            <Link to="/rooms/sindu-kamare/heritage" data-testid="heritage-link"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full font-ui font-bold text-sm transition-all active:scale-95 hover:-translate-y-0.5"
              style={{ background: "rgba(255,255,255,0.06)", color: "#F5F3FF", border: "1px solid rgba(255,255,255,0.16)" }}>
              <Library size={18} style={{ color: "var(--sigiriya-gold)" }} /> {t("heritage_wall")}
            </Link>
            <Link to="/rooms/sindu-kamare/serenbity" data-testid="serenbity-link"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full font-ui font-bold text-sm transition-all active:scale-95 hover:-translate-y-0.5"
              style={{ background: "linear-gradient(135deg, var(--sigiriya-gold) 0%, var(--vermilion) 70%)", color: "#1a0a05", border: "1px solid transparent" }}>
              <Film size={18} /> Serenbity · Sri Lanka
            </Link>
            <Link to="/rooms/sindu-kamare/about" data-testid="about-link"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full font-ui font-bold text-sm transition-all active:scale-95 hover:-translate-y-0.5"
              style={{ background: "rgba(255,255,255,0.06)", color: "#F5F3FF", border: "1px solid rgba(255,255,255,0.16)" }}>
              <Sparkles size={18} style={{ color: "var(--sigiriya-gold)" }} /> About Neox
            </Link>
            <Link to="/rooms/sindu-kamare/ambassadors" data-testid="ambassadors-link"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full font-ui font-bold text-sm transition-all active:scale-95 hover:-translate-y-0.5"
              style={{ background: "linear-gradient(130deg, var(--sigiriya-gold), var(--gold-2))", color: "#1a0a05", border: "1px solid transparent" }}>
              <Crown size={18} /> Ambassadors
            </Link>
            </div>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="max-w-6xl mx-auto px-6 py-20 text-center font-ui text-lg" style={{ color: "var(--text-muted)" }}>{t("loading")}</div>
      ) : songs.length === 0 ? (
        <div className="max-w-6xl mx-auto px-6 py-20 text-center font-ui text-lg" style={{ color: "var(--text-muted)" }}>{t("no_songs")}</div>
      ) : (
        <>
          {/* Song of the day */}
          <SongOfTheDay song={sotd} />

          {/* Next pension payday — gentle helper for elders */}
          <PensionPayday />

          {/* Poya day observance */}
          <PoyaBanner
            entry={poyaEntry}
            onPlayLamp={() => { const lamp = poyaPlaylist(songs); if (lamp.length) playIndex(0, lamp); }}
          />
          {poyaEntry?.poya?.meheni && <MeheniTribute songs={songs} />}

          {/* Featured strip */}
          {featured.length > 0 && (
            <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
              <div className="mb-4">
                <h2 className={`${lang === "si" ? "font-si" : "font-en"} text-2xl sm:text-3xl font-bold`} style={{ color: "var(--text)" }}>{t("featured")}</h2>
                <p className="font-ui text-sm mt-1" style={{ color: "var(--text-muted)" }}>{t("featured_sub")}</p>
              </div>
              <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1 sm:grid sm:grid-cols-3">
                {featured.map((song) => {
                  const idx = songs.findIndex((s) => s.id === song.id);
                  const primary = ctitle(song);
                  return (
                    <button
                      key={song.id}
                      onClick={() => playIndex(idx >= 0 ? idx : 0, songs)}
                      data-testid={`featured-card-${song.id}`}
                      className="text-left shrink-0 w-[260px] sm:w-auto rounded-3xl p-4 transition-transform hover:scale-[1.02] active:scale-95"
                      style={{ background: "var(--card)", border: "2px solid var(--border)" }}
                    >
                      <div className="relative">
                        <img src={song.cover_url} alt={song.title_en} className="w-full aspect-square rounded-2xl object-cover" />
                        <span className="absolute bottom-2 right-2 grid place-items-center w-12 h-12 rounded-full sk-glow" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}>
                          <Play size={22} fill="currentColor" style={{ marginLeft: 2 }} />
                        </span>
                      </div>
                      <p className={`${fontClass()} text-xl font-bold mt-3 truncate`} style={{ color: "var(--text)" }}>{primary}</p>
                      <p className="font-ui text-sm truncate" style={{ color: "var(--text-muted)" }}>{song.artist}</p>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Collections / albums */}
          {collections.length > 0 && (
            <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10">
              <div className="mb-4">
                <h2 className={`${fontClass()} text-2xl sm:text-3xl font-bold flex items-center gap-2`} style={{ color: "var(--text)" }}><Library size={26} style={{ color: "var(--accent)" }} /> {t("col_title")}</h2>
                <p className="font-ui text-sm mt-1" style={{ color: "var(--text-muted)" }}>{t("col_sub")}</p>
              </div>
              <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1 sm:grid sm:grid-cols-3">
                {collections.map((c) => (
                  <Link
                    key={c.id}
                    to={`/rooms/sindu-kamare/collection/${c.id}`}
                    data-testid={`collection-card-${c.id}`}
                    className="text-left shrink-0 w-[240px] sm:w-auto rounded-3xl p-4 transition-transform hover:scale-[1.02] active:scale-95"
                    style={{ background: "var(--card)", border: "2px solid var(--border)" }}
                  >
                    <img src={c.cover_url} alt="" className="w-full aspect-square rounded-2xl object-cover" />
                    <p className={`${fontClass()} text-lg font-bold mt-3 truncate`} style={{ color: "var(--text)" }}>{ctitle(c)}</p>
                    <p className="font-ui text-sm" style={{ color: "var(--text-muted)" }}>{c.count} {t("col_songs")}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Featured dedication */}
          {featuredDed && featuredDed.song && (
            <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10">
              <Link to={`/rooms/sindu-kamare/dedication/${featuredDed.id}`} data-testid="room-featured-dedication" className="block rounded-3xl p-6 sm:p-8 transition-transform hover:scale-[1.01]" style={{ background: "var(--brass-light)", border: "2px solid var(--accent-glow)" }}>
                <div className="flex items-center gap-4">
                  <img src={featuredDed.song.cover_url} alt="" className="w-16 h-16 rounded-2xl object-cover shrink-0" />
                  <div className="min-w-0">
                    <p className="font-ui text-xs font-bold uppercase tracking-wide flex items-center gap-1" style={{ color: "var(--accent)" }}><Heart size={13} fill="currentColor" /> {t("dd_featured")}</p>
                    <p className={`${fontClass()} text-lg font-bold truncate`} style={{ color: "var(--text)" }}>{t("dd_for")} {featuredDed.to_name} — {ctitle(featuredDed.song)}</p>
                    {featuredDed.message && <p className="font-si text-sm truncate" style={{ color: "var(--text-2)" }}>“{featuredDed.message}” — {featuredDed.from_name}</p>}
                  </div>
                </div>
              </Link>
            </section>
          )}

          {/* Evening Blessing banner */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10">
            <Link to="/rooms/sindu-kamare/evening" data-testid="room-evening-banner" className="block rounded-3xl p-6 sm:p-8 transition-transform hover:scale-[1.01]" style={{ background: "linear-gradient(120deg, #2A1B0E 0%, #4A2E12 60%, #B45309 140%)", color: "#FFFDF7" }}>
              <div className="flex items-center gap-4">
                <span className="grid place-items-center w-14 h-14 rounded-2xl shrink-0 sk-glow" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}><Moon size={26} /></span>
                <div className="min-w-0">
                  <h3 className={`${lang === "si" ? "font-si" : "font-en"} text-xl sm:text-2xl font-bold`}>{t("eb_title")} 🪔</h3>
                  <p className="font-ui text-sm mt-1" style={{ color: "rgba(255,255,255,0.75)" }}>{t("eb_sub")}</p>
                </div>
              </div>
            </Link>
          </section>

          {/* Rooms hub */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10">
            <div className="mb-4">
              <h2 className={`${fontClass()} text-2xl sm:text-3xl font-bold`} style={{ color: "var(--text)" }}>{t("rooms_title")}</h2>
              <p className="font-ui text-sm mt-1" style={{ color: "var(--text-muted)" }}>{t("rooms_sub")}</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {ROOMS.map((r) => {
                const Icon = r.icon;
                return (
                  <Link key={r.to} to={r.to} data-testid={r.testid} className="rounded-3xl p-5 transition-transform hover:scale-[1.02] active:scale-95" style={{ background: r.grad, color: "#FFFDF7", minHeight: 150 }}>
                    <span className="grid place-items-center w-12 h-12 rounded-2xl sk-glow" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}><Icon size={24} /></span>
                    <h3 className={`${fontClass()} text-lg sm:text-xl font-bold mt-4 leading-snug`}>{t(r.titleKey)}</h3>
                    <p className="font-ui text-xs sm:text-sm mt-1 line-clamp-2" style={{ color: "rgba(255,255,255,0.75)" }}>{t(r.subKey)}</p>
                  </Link>
                );
              })}
            </div>
          </section>

          {/* Family across the miles */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10">
            <div className="mb-4">
              <h2 className={`${fontClass()} text-2xl sm:text-3xl font-bold`} style={{ color: "var(--text)" }}>{t("fam_title")}</h2>
              <p className="font-ui text-sm mt-1" style={{ color: "var(--text-muted)" }}>{t("fam_sub")}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link to="/rooms/sindu-kamare/voice" data-testid="family-voice-card" className="flex items-center gap-4 rounded-3xl p-5 transition-transform hover:scale-[1.02]" style={{ background: "linear-gradient(135deg, #1C140E, #92400E 160%)", color: "#FFFDF7" }}>
                <span className="grid place-items-center w-14 h-14 rounded-2xl shrink-0 sk-glow" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}><Mic size={26} /></span>
                <div className="min-w-0"><h3 className={`${fontClass()} text-lg sm:text-xl font-bold`}>{t("voice_nav")} 🎙️</h3><p className="font-ui text-xs sm:text-sm mt-1 line-clamp-2" style={{ color: "rgba(255,255,255,0.75)" }}>{t("voice_sub")}</p></div>
              </Link>
              <Link to="/remembrance" data-testid="family-remembrance-card" className="flex items-center gap-4 rounded-3xl p-5 transition-transform hover:scale-[1.02]" style={{ background: "linear-gradient(135deg, #0C0A08, #4A2E12 160%)", color: "#FFFDF7" }}>
                <span className="grid place-items-center w-14 h-14 rounded-2xl shrink-0 sk-glow" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}><Flame size={26} /></span>
                <div className="min-w-0"><h3 className={`${fontClass()} text-lg sm:text-xl font-bold`}>{t("rem_title")} 🪔</h3><p className="font-ui text-xs sm:text-sm mt-1 line-clamp-2" style={{ color: "rgba(255,255,255,0.75)" }}>{t("rem_sub")}</p></div>
              </Link>
            </div>
          </section>

          {/* Workshop banner */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10">
            <Link to="/rooms/sindu-kamare/workshop" data-testid="room-workshop-banner" className="block rounded-3xl p-6 sm:p-8 transition-transform hover:scale-[1.01]" style={{ background: "var(--player-bg)", color: "var(--player-text)" }}>
              <div className="flex items-center gap-4">
                <span className="grid place-items-center w-14 h-14 rounded-2xl shrink-0 sk-glow" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}><Users size={26} /></span>
                <div className="min-w-0">
                  <h3 className={`${lang === "si" ? "font-si" : "font-en"} text-xl sm:text-2xl font-bold`}>{t("ws_title")}</h3>
                  <p className="font-ui text-sm mt-1" style={{ color: "rgba(255,255,255,0.7)" }}>{t("ws_sub")}</p>
                </div>
              </div>
            </Link>
          </section>

          {/* Full list */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10" data-testid="dedications-banner-wrap">
            <Link to="/rooms/sindu-kamare/dedications" data-testid="room-dedications-banner" className="block rounded-3xl p-6 sm:p-8 mb-2 transition-transform hover:scale-[1.01]" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
              <div className="flex items-center gap-4">
                <span className="grid place-items-center w-14 h-14 rounded-2xl shrink-0" style={{ background: "var(--accent)", color: "#fff" }}><Heart size={24} /></span>
                <div className="min-w-0">
                  <h3 className={`${lang === "si" ? "font-si" : "font-en"} text-xl sm:text-2xl font-bold`} style={{ color: "var(--text)" }}>{t("dd_title")}</h3>
                  <p className="font-ui text-sm mt-1" style={{ color: "var(--text-muted)" }}>{t("dd_sub")}</p>
                </div>
              </div>
            </Link>
          </section>

          {/* Song list */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10">
            <LiyaPataDivider className="mb-5 mx-auto max-w-xs" testid="room-liyapata-divider" />
            <div className="mb-4 flex items-center gap-3">
              <LotusMedallion size={34} style={{ color: "var(--accent-3)" }} className="shrink-0 sk-float" testid="room-lotus-badge" />
              <div>
                <h2 className={`${lang === "si" ? "font-si" : "sk-display"} text-2xl sm:text-3xl font-extrabold sk-gold-foil`}>{t("full_list")}</h2>
                <p className="font-ui text-sm mt-1" style={{ color: "var(--text-muted)" }}>{t("full_list_sub")}</p>
              </div>
            </div>

            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-4">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  data-testid={`filter-${f.key}`}
                  className="shrink-0 h-11 px-5 rounded-full font-ui font-bold text-sm transition-all duration-300"
                  style={{
                    background: filter === f.key ? f.color : "var(--card)",
                    color: filter === f.key ? "#fff" : "var(--text-2)",
                    border: filter === f.key ? `1px solid ${f.color}` : "1px solid var(--border)",
                    boxShadow: filter === f.key ? "0 8px 20px -10px var(--border-glow)" : "none",
                  }}
                >
                  {t(f.labelKey)}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {filter === "fav" && filtered.length === 0 ? (
                <p className="font-ui text-base py-8 text-center" style={{ color: "var(--text-muted)" }}>{t("fav_empty")}</p>
              ) : (
                filtered.map((song) => {
                  const realIndex = songs.findIndex((s) => s.id === song.id);
                  return <SongRow key={song.id} song={song} index={realIndex} list={filter === "fav" ? filtered : songs} />;
                })
              )}
            </div>
          </section>
        </>
      )}

      <SponsorCard />

      <VisionCredits
        onShare={async () => {
          const url = window.location.origin + "/rooms/sindu-kamare";
          const data = { title: "Neox — Music Room", text: "A calm music room made with love for our elders. Come and listen.", url };
          try {
            if (navigator.share) await navigator.share(data);
            else { await navigator.clipboard.writeText(url); }
          } catch (e) { /* user dismissed */ }
        }}
      />

      {current && <div style={{ height: 8 }} />}
      <PlayerBar />
      {showBlessing && (
        <BlessingOverlay
          onClose={() => setShowBlessing(false)}
          onLight={() => { const lamp = todaysLamp(songs); if (lamp.length) playIndex(0, lamp); setShowBlessing(false); }}
        />
      )}
    </div>
  );
}
